import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { mockData } from '../mockData';
import { Shield, FileText, Building2, Search, ShoppingBag, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const ServicesSlider = ({ onOpenModal }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const iconMap = { Shield, FileText, Building2, Search, ShoppingBag };

  const serviceBackgrounds = {
    1: 'https://images.unsplash.com/photo-1589330694653-ded6df03f754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHx0cmFkZW1hcmslMjBpbnRlbGxlY3R1YWwlMjBwcm9wZXJ0eSUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzM5NDAwODJ8MA&ixlib=rb-4.1.0&q=85',
    2: 'https://images.unsplash.com/photo-1639784120039-11f859ea1612?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxjb3B5cmlnaHQlMjBjcmVhdGl2ZSUyMHdvcmslMjBwcm90ZWN0aW9uJTIwZGlnaXRhbCUyMGFydHxlbnwwfHx8fDE3NzM5NDAxMTF8MA&ixlib=rb-4.1.0&q=85',
    3: 'https://images.unsplash.com/photo-1760611656615-db3fad24a314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxzdGFydHVwJTIwZW50cmVwcmVuZXVyJTIwZWNvbW1lcmNlJTIwYnVzaW5lc3MlMjBncm93dGh8ZW58MHx8fHwxNzczOTQwMDkwfDA&ixlib=rb-4.1.0&q=85',
    4: 'https://images.unsplash.com/photo-1510034696085-597d716bd162?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHx0cmFkZW1hcmslMjBpbnRlbGxlY3R1YWwlMjBwcm9wZXJ0eSUyMGRvY3VtZW50JTIwc2lnbmluZ3xlbnwwfHx8fDE3NzM5NDAwODJ8MA&ixlib=rb-4.1.0&q=85',
    5: 'https://images.unsplash.com/photo-1770013413878-2530e2c3d82b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxzdGFydHVwJTIwZW50cmVwcmVuZXVyJTIwZWNvbW1lcmNlJTIwYnVzaW5lc3MlMjBncm93dGh8ZW58MHx8fHwxNzczOTQwMDkwfDA&ixlib=rb-4.1.0&q=85'
  };

  const serviceRoutes = {
    1: '/trademark-registration',
    2: '/copyright-registration',
    3: '/business-formation',
    4: '/trademark-search',
    5: '/amazon-brand-registry'
  };

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section id="services" className="md:py-20 py-4 bg-navy-800 relative overflow-hidden">
      <div className="absolute top-20 left-10 w-72 h-72 bg-navy-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-navy-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
            What We{' '}
            <span className="bg-brand-gradient bg-clip-text text-transparent">Do Best</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Five core services that cover every stage of your brand's legal protection
          </p>
        </div>

        <div className="relative md:px-12 px-6">
          {/* Arrows - inside container, vertically centered */}
          <Button
            onClick={scrollPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 bg-navy-700 hover:bg-gold-400 hover:text-navy-900 text-gold-400 rounded-full w-10 h-10 p-0 shadow-lg z-10 border border-gold-400/40"
            size="icon"
            data-no-hover="true"
          >
            <ChevronLeft size={20} />
          </Button>
          <Button
            onClick={scrollNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 bg-navy-700 hover:bg-gold-400 hover:text-navy-900 text-gold-400 rounded-full w-10 h-10 p-0 shadow-lg z-10 border border-gold-400/40"
            size="icon"
            data-no-hover="true"
          >
            <ChevronRight size={20} />
          </Button>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {mockData.services.map((service) => {
                const Icon = iconMap[service.icon];
                return (
                  <div key={service.id} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 px-3">
                    <div className="group relative rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-navy-500/30 overflow-hidden bg-navy-700 h-[420px] flex flex-col">
                      {/* Background Image */}
                      <div
                        className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-20 transition-opacity duration-500"
                        style={{ backgroundImage: `url(${serviceBackgrounds[service.id]})` }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-navy-700/95 to-navy-800/98" />

                      {/* Content */}
                      <div className="relative z-10 p-6 flex flex-col flex-1">
                        <div className="bg-brand-gradient w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                          <Icon className="text-white" size={24} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-100 mb-2 group-hover:text-gold-400 transition-colors">{service.title}</h3>
                        <p className="text-sm text-gray-300 mb-4 leading-relaxed flex-1">{service.description}</p>
                        <div className="space-y-1.5 mb-4">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-gold-400 rounded-full shrink-0" />
                              <span className="text-gray-200 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                        <Link to={serviceRoutes[service.id]} className="mt-auto">
                          <Button
                            variant="ghost"
                            className="text-gold-400 hover:text-navy-900 hover:bg-gold-400 px-4 py-2 border border-gold-400/50 transition-all text-sm"
                          >
                            Learn More <ArrowRight className="ml-1.5" size={14} />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {mockData.services.map((_, index) => (
            <button
              key={index}
              data-no-hover="true"
              className={`h-2 rounded-full transition-all duration-300 ${
                index === selectedIndex ? 'bg-gold-400 w-6' : 'bg-navy-600 w-2 hover:bg-gold-400/50'
              }`}
              onClick={() => emblaApi && emblaApi.scrollTo(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSlider;
